import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Rating,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Alert,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  ShoppingCart,
  Favorite,
  Add,
  Remove,
  ArrowBack,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Review {
  id: number;
  rating: number;
  comment: string;
  created_at: string;
  first_name: string;
  last_name: string;
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_name: string;
  stock_quantity: number;
  reviews: Review[];
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (value > 0 && (!product || value <= product.stock_quantity)) {
      setQuantity(value);
    }
  };

  const increaseQuantity = () => {
    if (product && quantity < product.stock_quantity) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!product) return;

    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/cart/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          product_id: product.id,
          quantity,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Product added to cart!',
          severity: 'success',
        });
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setSnackbar({
          open: true,
          message: errorData.error || 'Failed to add to cart',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setSnackbar({
        open: true,
        message: 'Error adding to cart',
        severity: 'error',
      });
    }
  };

  const handleAddToWishlist = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!product) return;

    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/wishlist/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: product.id,
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Product added to wishlist!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      setSnackbar({
        open: true,
        message: 'Error adding to wishlist',
        severity: 'error',
      });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return <Typography>Loading product details...</Typography>;
  }

  if (!product) {
    return <Typography>Product not found</Typography>;
  }

  const averageRating = product.reviews && product.reviews.length > 0
    ? product.reviews.reduce((sum, review) => sum + review.rating, 0) / product.reviews.length
    : 0;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/shop" underline="hover">
          Shop
        </Link>
        <Link color="inherit" href={`/shop?category=${product.category_name}`} underline="hover">
          {product.category_name}
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/shop')}
        sx={{ mb: 3 }}
      >
        Back to Shop
      </Button>

      <Grid container spacing={4}>
        {/* Product Image */}
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={product.image_url || 'https://via.placeholder.com/600x600?text=Product+Image'}
            alt={product.name}
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          />
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {product.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Rating value={averageRating} precision={0.5} readOnly />
            <Typography variant="body2" sx={{ ml: 1 }}>
              ({product.reviews ? product.reviews.length : 0} reviews)
            </Typography>
          </Box>

          <Typography variant="h5" color="#E91E63" fontWeight={700} sx={{ mb: 2 }}>
            ${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            {product.description}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Availability:
            </Typography>
            <Typography variant="body1" color={product.stock_quantity > 0 ? 'success.main' : 'error.main'}>
              {product.stock_quantity > 0 ? `In Stock (${product.stock_quantity} available)` : 'Out of Stock'}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Quantity:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={decreaseQuantity} disabled={quantity <= 1}>
                <Remove />
              </IconButton>
              <TextField
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                inputProps={{ min: 1, max: product.stock_quantity }}
                sx={{ width: 60, mx: 1 }}
              />
              <IconButton onClick={increaseQuantity} disabled={quantity >= product.stock_quantity}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ShoppingCart />}
              onClick={handleAddToCart}
              disabled={product.stock_quantity <= 0}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #AD1457 0%, #880E4F 100%)',
                },
              }}
            >
              Add to Cart
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Favorite />}
              onClick={handleAddToWishlist}
              sx={{ color: '#E91E63', borderColor: '#E91E63' }}
            >
              Wishlist
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary">
            Category: {product.category_name}
          </Typography>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Box sx={{ mt: 6 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{ mb: 3 }}
        >
          <Tab label="Description" />
          <Tab label={`Reviews (${product.reviews ? product.reviews.length : 0})`} />
        </Tabs>

        {activeTab === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1">{product.description}</Typography>
            </CardContent>
          </Card>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              {product.reviews && product.reviews.length > 0 ? (
                product.reviews.map((review) => (
                  <Box key={review.id} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {review.first_name} {review.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(review.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Rating value={review.rating} readOnly size="small" sx={{ my: 1 }} />
                    <Typography variant="body2">{review.comment}</Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography>No reviews yet.</Typography>
              )}

              {user && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Write a Review
                  </Typography>
                  <Rating
                    name="new-review-rating"
                    value={0}
                    onChange={(event, newValue) => {
                      // Handle rating change
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Share your thoughts about this product..."
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                    }}
                  >
                    Submit Review
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as 'success' | 'error'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDetail;
